import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useInView, useReducedMotion, animate } from "framer-motion";
import { toast } from "sonner";
import {
  ArrowRight, Pill, Warehouse, Plane, FileText, Truck, ShieldCheck,
  CheckCircle2, Send, MapPin, Mail, Phone, Globe2, Snowflake, Clock,
} from "lucide-react";
import { api } from "@/lib/api";
import { Divider } from "@/components/marketing/illustrations";
import { WhatsAppIcon, WA_LINK } from "@/components/marketing/WhatsAppIcon";
import { LatamScene } from "@/components/marketing/LatamScene";

/* ---------- motion helpers ---------- */
export const useReveal = () => {
  const reduce = useReducedMotion();
  return (d = 0) =>
    reduce
      ? {}
      : {
          initial: { opacity: 0, y: 26 },
          whileInView: { opacity: 1, y: 0 },
          viewport: { once: true, margin: "-80px" },
          transition: { duration: 0.7, delay: d, ease: [0.22, 1, 0.36, 1] },
        };
};

const Eyebrow = ({ children }) => (
  <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-[#377DD3]">
    <span className="h-px w-6 bg-[#F5C64D]" /> {children}
  </span>
);

/* ---------- HERO ---------- */
export function Hero() {
  const reduce = useReducedMotion();
  const reveal = useReveal();
  return (
    <section id="top" className="relative overflow-hidden bg-[#F8FAFC] pt-[72px]">
      <div className="pointer-events-none absolute inset-0" aria-hidden="true">
        <div className="absolute -top-24 right-[-10%] h-[520px] w-[520px] rounded-full bg-[#377DD3]/10 blur-3xl" />
        <div className="absolute bottom-[-20%] left-[-10%] h-[420px] w-[420px] rounded-full bg-[#F5C64D]/10 blur-3xl" />
        <div className="absolute inset-0 opacity-[0.4] [background-image:linear-gradient(to_right,#D7E3F2_1px,transparent_1px),linear-gradient(to_bottom,#D7E3F2_1px,transparent_1px)] [background-size:56px_56px] [mask-image:radial-gradient(ellipse_at_center,black,transparent_75%)]" />
      </div>

      <div className="relative mx-auto grid max-w-7xl grid-cols-1 items-center gap-10 px-4 py-16 sm:px-6 lg:grid-cols-12 lg:gap-8 lg:px-8 lg:py-28">
        <div className="lg:col-span-7">
          <motion.div {...reveal(0)}><Eyebrow>Dubai DMCC • Global B2B Supply</Eyebrow></motion.div>
          <motion.h1 {...reveal(0.06)} className="font-heading mt-5 text-4xl font-extrabold leading-[1.05] tracking-tight text-[#0D1B2A] sm:text-5xl lg:text-6xl">
            Global pharmaceutical supply,{" "}
            <span className="bg-gradient-to-r from-[#377DD3] to-[#245FAF] bg-clip-text text-transparent">precision cold-chain</span>{" "}
            logistics.
          </motion.h1>
          <motion.p {...reveal(0.12)} className="mt-6 max-w-xl text-base leading-relaxed text-[#64748B] sm:text-lg">
            Heliopolis Pharmaceuticals connects the world's leading manufacturers with hospitals,
            wholesalers and licensed distributors — specialising in named patient supply and B2B
            export to Latin America, MENA and Africa, with GDP-compliant cold-chain handling and real-time portal tracking.
          </motion.p>
          <motion.div {...reveal(0.18)} className="mt-9 flex flex-col gap-3 sm:flex-row sm:items-center">
            <Link to="/login" data-testid="hero-primary-cta" className="group inline-flex items-center justify-center gap-2 rounded-full bg-gradient-to-r from-[#377DD3] to-[#245FAF] px-7 py-3.5 text-base font-semibold text-white shadow-[0_14px_34px_-12px_rgba(55,125,211,0.9)] ring-1 ring-inset ring-[#F5C64D]/50 transition-all hover:scale-[1.02]">
              Enter Portal <ArrowRight size={18} className="transition-transform group-hover:translate-x-0.5" />
            </Link>
            <a href="#contact" data-testid="hero-secondary-cta" className="inline-flex items-center justify-center gap-2 rounded-full border border-[#D7E3F2] bg-white px-7 py-3.5 text-base font-semibold text-[#0D1B2A] transition-all hover:border-[#377DD3]/50 hover:bg-[#EAF2FB]">
              Contact Us
            </a>
          </motion.div>

          <motion.div {...reveal(0.26)} className="mt-12 grid max-w-lg grid-cols-3 gap-6">
            {[
              { k: "40+", v: "Named patient countries" },
              { k: "3", v: "Regions: LATAM · MENA · Africa" },
              { k: "24/7", v: "Portal tracking" },
            ].map((s) => (
              <div key={s.v}>
                <div className="font-heading text-2xl font-extrabold text-[#0D1B2A] sm:text-3xl">{s.k}</div>
                <div className="mt-1 text-xs text-[#64748B]">{s.v}</div>
              </div>
            ))}
          </motion.div>
        </div>

        <div className="lg:col-span-5">
          <motion.div
            initial={reduce ? {} : { opacity: 0, scale: 0.92 }}
            animate={reduce ? {} : { opacity: 1, scale: 1 }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="relative"
          >
            <motion.div animate={reduce ? {} : { y: [0, -14, 0] }} transition={{ duration: 7, repeat: Infinity, ease: "easeInOut" }}>
              <LatamScene className="w-full drop-shadow-[0_30px_60px_rgba(36,95,175,0.25)]" />
            </motion.div>
          </motion.div>
        </div>
      </div>
      <Divider className="h-10 w-full" />
    </section>
  );
}

/* ---------- ABOUT ---------- */
export function About() {
  const reveal = useReveal();
  const points = [
    { icon: ShieldCheck, t: "GDP & regulatory compliance", d: "Full audit trails, batch verification, COA documentation and MoHAP alignment." },
    { icon: Snowflake, t: "End-to-end cold-chain assurance", d: "Temperature-monitored from warehouse to last-mile, with live deviation alerts." },
    { icon: Globe2, t: "Global reach, regional trust", d: "A Dubai DMCC hub bridging international suppliers with regional healthcare." },
  ];
  return (
    <section id="about" className="scroll-mt-20 bg-white py-20 lg:py-28">
      <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-12 px-4 sm:px-6 lg:grid-cols-12 lg:gap-16 lg:px-8">
        <motion.div {...reveal(0)} className="relative lg:col-span-5">
          <div className="absolute -left-4 -top-4 h-24 w-24 rounded-2xl bg-[#F5C64D]/15" aria-hidden="true" />
          <div className="relative flex aspect-[440/340] items-center justify-center rounded-3xl border border-[#D7E3F2] bg-[#F8FAFC] p-6 shadow-[0_30px_60px_-30px_rgba(13,27,42,0.25)]">
            <img src="/heliopolis-logo.png" alt="Heliopolis Pharmaceuticals" className="max-h-[70%] w-auto max-w-[80%] object-contain" data-testid="about-logo" />
          </div>
        </motion.div>
        <div className="lg:col-span-7">
          <motion.div {...reveal(0)}><Eyebrow>Who we are</Eyebrow></motion.div>
          <motion.h2 {...reveal(0.06)} className="font-heading mt-4 text-3xl font-bold tracking-tight text-[#0D1B2A] sm:text-4xl">
            Empowering global healthcare from the heart of Dubai.
          </motion.h2>
          <motion.p {...reveal(0.1)} className="mt-5 text-base leading-relaxed text-[#64748B] sm:text-lg">
            We are a licensed B2B pharmaceutical merchant and logistics specialist based in the Dubai
            Multi Commodities Centre. Our mission is simple: move critical medicines across the world
            reliably, compliantly and transparently — so healthcare providers never run short.
          </motion.p>
          <div className="mt-8 space-y-5">
            {points.map((p, i) => (
              <motion.div key={p.t} {...reveal(0.12 + i * 0.06)} className="flex gap-4">
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-[#377DD3] to-[#245FAF] text-white shadow-md">
                  <p.icon size={20} />
                </div>
                <div>
                  <h3 className="font-semibold text-[#0D1B2A]">{p.t}</h3>
                  <p className="mt-1 text-sm text-[#64748B]">{p.d}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ---------- SERVICES ---------- */
const SERVICES = [
  { icon: Pill, t: "Pharmaceutical Wholesale Supply", d: "Direct sourcing and global bulk distribution of certified brand and generic therapeutics.", span: "lg:col-span-2" },
  { icon: Warehouse, t: "Multi-Warehouse Cold Stock", d: "Monitored storage across +2°C to +8°C, +15°C to +25°C and −20°C deep-freeze zones." },
  { icon: Plane, t: "Global Express Dispatch", d: "Integrated courier delivery via FedEx, UPS, DHL and specialised air-cargo charters." },
  { icon: FileText, t: "Consolidated Invoicing", d: "Multi-currency settlement, trade-credit support and streamlined PO accounting." },
  { icon: Truck, t: "Real-Time Order Tracking", d: "24/7 portal access with line-item dispatch updates, AWB tracking and digital POD.", span: "lg:col-span-2" },
];

export function Services() {
  const reveal = useReveal();
  return (
    <section id="services" className="scroll-mt-20 bg-[#F8FAFC] py-20 lg:py-28">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl">
          <motion.div {...reveal(0)}><Eyebrow>What we do</Eyebrow></motion.div>
          <motion.h2 {...reveal(0.06)} className="font-heading mt-4 text-3xl font-bold tracking-tight text-[#0D1B2A] sm:text-4xl">
            Comprehensive B2B supply services.
          </motion.h2>
          <motion.p {...reveal(0.1)} className="mt-4 text-base text-[#64748B] sm:text-lg">
            One partner across the entire pharmaceutical supply chain — from sourcing to the final mile.
          </motion.p>
        </div>

        <div className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
          {SERVICES.map((s, i) => (
            <motion.div
              key={s.t}
              {...reveal(0.06 + (i % 3) * 0.05)}
              className={`group relative overflow-hidden rounded-3xl border border-[#D7E3F2] bg-white p-7 transition-all hover:-translate-y-1 hover:border-[#377DD3]/40 hover:shadow-[0_28px_50px_-24px_rgba(36,95,175,0.4)] ${s.span || ""}`}
              data-testid={`service-card-${i}`}
            >
              <div className="absolute right-0 top-0 h-24 w-24 translate-x-8 -translate-y-8 rounded-full bg-[#377DD3]/5 transition-transform group-hover:scale-150" aria-hidden="true" />
              <div className="relative flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br from-[#377DD3] to-[#245FAF] text-white shadow-lg">
                <s.icon size={22} />
              </div>
              <h3 className="relative mt-5 text-lg font-semibold text-[#0D1B2A]">{s.t}</h3>
              <p className="relative mt-2 text-sm leading-relaxed text-[#64748B]">{s.d}</p>
            </motion.div>
          ))}
          <motion.div {...reveal(0.2)} className="relative flex flex-col justify-between overflow-hidden rounded-3xl bg-gradient-to-br from-[#377DD3] to-[#245FAF] p-7 text-white">
            <div>
              <ShieldCheck size={26} className="text-[#F5C64D]" />
              <h3 className="mt-4 text-lg font-semibold">GDP & Compliance Management</h3>
              <p className="mt-2 text-sm text-white/80">Regulatory audit trails, batch verification and full documentation on every shipment.</p>
            </div>
            <Link to="/login" className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-white hover:text-[#F5C64D]" data-testid="services-portal-cta">
              Access the portal <ArrowRight size={15} />
            </Link>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

/* ---------- WHY US (stats with counters) ---------- */
function Counter({ to, suffix = "", decimals = 0 }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  const reduce = useReducedMotion();
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!inView) return;
    if (reduce) { setVal(to); return; }
    const controls = animate(0, to, {
      duration: 1.6,
      ease: [0.22, 1, 0.36, 1],
      onUpdate: (v) => setVal(v),
    });
    return () => controls.stop();
  }, [inView, to, reduce]);
  return <span ref={ref}>{val.toLocaleString(undefined, { minimumFractionDigits: decimals, maximumFractionDigits: decimals })}{suffix}</span>;
}

const STATS = [
  { to: 85, suffix: "+", label: "Global destinations", sub: "Countries worldwide", icon: Globe2 },
  { to: 99.4, suffix: "%", decimals: 1, label: "On-time delivery", sub: "Cold-chain audited", icon: Clock },
  { to: 12000, suffix: " m²", label: "Warehouse capacity", sub: "DMCC & regional hubs", icon: Warehouse },
  { to: 99.9, suffix: "%", decimals: 1, label: "Order accuracy", sub: "ERP automated checks", icon: CheckCircle2 },
];

export function WhyUs() {
  const reveal = useReveal();
  return (
    <section id="why-us" className="scroll-mt-20 relative overflow-hidden bg-[#0D1B2A] py-20 text-white lg:py-28">
      <div className="pointer-events-none absolute inset-0" aria-hidden="true">
        <div className="absolute -right-20 top-0 h-96 w-96 rounded-full bg-[#377DD3]/20 blur-3xl" />
        <div className="absolute bottom-0 left-10 h-72 w-72 rounded-full bg-[#F5C64D]/10 blur-3xl" />
      </div>
      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl">
          <motion.div {...reveal(0)}>
            <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-[#F5C64D]">
              <span className="h-px w-6 bg-[#F5C64D]" /> Why Heliopolis
            </span>
          </motion.div>
          <motion.h2 {...reveal(0.06)} className="font-heading mt-4 text-3xl font-bold tracking-tight sm:text-4xl">
            Trust built on measurable performance.
          </motion.h2>
          <motion.p {...reveal(0.1)} className="mt-4 text-base text-white/70 sm:text-lg">
            Reliability, compliance and transparency — the numbers our partners count on.
          </motion.p>
        </div>

        <div className="mt-14 grid grid-cols-2 gap-6 lg:grid-cols-4">
          {STATS.map((s, i) => (
            <motion.div key={s.label} {...reveal(0.08 + i * 0.06)} className="rounded-3xl border border-white/10 bg-white/5 p-6 backdrop-blur-sm" data-testid={`stat-card-${i}`}>
              <s.icon size={22} className="text-[#F5C64D]" />
              <div className="font-heading mt-4 text-3xl font-extrabold sm:text-4xl">
                <Counter to={s.to} suffix={s.suffix} decimals={s.decimals || 0} />
              </div>
              <div className="mt-2 text-sm font-semibold text-white">{s.label}</div>
              <div className="mt-0.5 text-xs text-white/55">{s.sub}</div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------- GLOBAL NETWORK ---------- */
export function Network() {
  const reveal = useReveal();
  return (
    <section id="network" className="scroll-mt-20 bg-white py-20 lg:py-28">
      <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-12 px-4 sm:px-6 lg:grid-cols-12 lg:px-8">
        <div className="lg:col-span-5">
          <motion.div {...reveal(0)}><Eyebrow>Global network</Eyebrow></motion.div>
          <motion.h2 {...reveal(0.06)} className="font-heading mt-4 text-3xl font-bold tracking-tight text-[#0D1B2A] sm:text-4xl">
            A Dubai hub, connected to the world.
          </motion.h2>
          <motion.p {...reveal(0.1)} className="mt-5 text-base leading-relaxed text-[#64748B] sm:text-lg">
            From our DMCC base we coordinate dispatch across the Gulf, Africa, Europe, Asia and the
            Americas — with trusted courier integrations and specialised cold-chain cargo routes.
          </motion.p>
          <div className="mt-8 flex flex-wrap gap-3">
            {["FedEx", "UPS", "DHL", "Air Cargo Charter"].map((c) => (
              <span key={c} className="rounded-full border border-[#D7E3F2] bg-[#F8FAFC] px-4 py-2 text-sm font-medium text-[#0D1B2A]">{c}</span>
            ))}
          </div>
        </div>
        <motion.div {...reveal(0.1)} className="relative lg:col-span-7">
          <div className="relative overflow-hidden rounded-3xl border border-[#D7E3F2] bg-gradient-to-b from-[#EAF2FB] to-white p-8">
            <img src="/world.svg" alt="Global dispatch network" className="w-full opacity-80 [filter:hue-rotate(5deg)]" onError={(e) => { e.currentTarget.style.display = "none"; }} />
            <div className="absolute left-[46%] top-[52%] flex -translate-x-1/2 -translate-y-1/2 flex-col items-center">
              <span className="relative flex h-4 w-4">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#F5C64D] opacity-60" />
                <span className="relative inline-flex h-4 w-4 rounded-full bg-[#F5C64D] ring-2 ring-white" />
              </span>
              <span className="mt-1.5 rounded-full bg-[#0D1B2A] px-2 py-0.5 text-[10px] font-semibold text-white">Dubai DMCC</span>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

/* ---------- CONTACT ---------- */
export function Contact() {
  const reveal = useReveal();
  const [form, setForm] = useState({ full_name: "", email: "", company: "", role: "", message: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const up = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.email || !form.message) {
      toast.error("Please fill in your name, email and message.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/contact", form);
      setDone(true);
      toast.success("Thank you — your enquiry has been received.");
    } catch (err) {
      toast.error(err?.response?.data?.detail?.[0]?.msg || "Could not send your enquiry. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const field = "w-full rounded-xl border border-[#D7E3F2] bg-white px-4 py-3 text-[#0D1B2A] placeholder:text-[#94A3B8] outline-none transition-colors focus:border-[#377DD3] focus:ring-4 focus:ring-[#377DD3]/10";

  return (
    <section id="contact" className="scroll-mt-20 bg-[#F8FAFC] py-20 lg:py-28">
      <div className="mx-auto grid max-w-7xl grid-cols-1 gap-10 px-4 sm:px-6 lg:grid-cols-12 lg:gap-8 lg:px-8">
        <motion.div {...reveal(0)} className="lg:col-span-7">
          <Eyebrow>Contact</Eyebrow>
          <h2 className="font-heading mt-4 text-3xl font-bold tracking-tight text-[#0D1B2A] sm:text-4xl">
            Talk to our global trade desk.
          </h2>
          <p className="mt-4 text-base text-[#64748B]">
            Tell us what you need — sourcing, dispatch or a supply partnership. We typically reply within one business day.
          </p>

          <div className="mt-8 rounded-3xl border border-[#D7E3F2] bg-white p-6 shadow-[0_30px_60px_-36px_rgba(13,27,42,0.3)] sm:p-8">
            {done ? (
              <div className="flex flex-col items-center py-10 text-center" data-testid="contact-success">
                <div className="flex h-16 w-16 items-center justify-center rounded-full bg-[#10B981]/10 text-[#10B981]">
                  <CheckCircle2 size={34} />
                </div>
                <h3 className="font-heading mt-5 text-xl font-bold text-[#0D1B2A]">Enquiry received</h3>
                <p className="mt-2 max-w-sm text-sm text-[#64748B]">
                  Thank you for reaching out to Heliopolis Pharmaceuticals. Our trade desk will be in touch shortly.
                </p>
                <button onClick={() => { setDone(false); setForm({ full_name: "", email: "", company: "", role: "", message: "" }); }} className="mt-6 text-sm font-semibold text-[#245FAF] hover:underline" data-testid="contact-reset">
                  Send another enquiry
                </button>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-4" data-testid="contact-form">
                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-[#0D1B2A]">Full name</label>
                    <input className={field} value={form.full_name} onChange={up("full_name")} placeholder="Jane Doe" data-testid="contact-name" />
                  </div>
                  <div>
                    <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-[#0D1B2A]">Work email</label>
                    <input type="email" className={field} value={form.email} onChange={up("email")} placeholder="you@company.com" data-testid="contact-email" />
                  </div>
                </div>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-[#0D1B2A]">Company</label>
                    <input className={field} value={form.company} onChange={up("company")} placeholder="Company / organisation" data-testid="contact-company" />
                  </div>
                  <div>
                    <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-[#0D1B2A]">Role <span className="text-[#94A3B8] normal-case">(optional)</span></label>
                    <input className={field} value={form.role} onChange={up("role")} placeholder="e.g. Procurement" data-testid="contact-role" />
                  </div>
                </div>
                <div>
                  <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-[#0D1B2A]">Message</label>
                  <textarea rows={4} className={field} value={form.message} onChange={up("message")} placeholder="How can we help?" data-testid="contact-message" />
                </div>
                <button type="submit" disabled={busy} className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-gradient-to-r from-[#377DD3] to-[#245FAF] px-7 py-3.5 text-base font-semibold text-white shadow-[0_14px_34px_-12px_rgba(55,125,211,0.9)] ring-1 ring-inset ring-[#F5C64D]/50 transition-all hover:scale-[1.01] disabled:opacity-60" data-testid="contact-submit">
                  {busy ? "Sending…" : <>Send enquiry <Send size={17} /></>}
                </button>
              </form>
            )}
          </div>
        </motion.div>

        <motion.div {...reveal(0.1)} className="lg:col-span-5">
          <div className="space-y-4">
            {[
              { icon: MapPin, t: "Visit us", d: "DMCC Free Zone, Jumeirah Lakes Towers, Dubai, UAE" },
              { icon: Mail, t: "Email", d: "customercare@heliopolispharmaceuticals.com", href: "mailto:customercare@heliopolispharmaceuticals.com", id: "email" },
              { icon: Phone, t: "Call", d: "+971 54 774 1436", href: "tel:+971547741436", id: "phone" },
              { icon: WhatsAppIcon, t: "WhatsApp", d: "+971 54 774 1436", href: WA_LINK, id: "whatsapp", wa: true },
            ].map((c) => (
              <div key={c.t} className="flex items-start gap-4 rounded-2xl border border-[#D7E3F2] bg-white p-5" data-testid={c.id ? `contact-info-${c.id}` : undefined}>
                <div className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${c.wa ? "bg-[#25D366]/15 text-[#128C7E]" : "bg-[#EAF2FB] text-[#245FAF]"}`}><c.icon size={20} /></div>
                <div className="min-w-0">
                  <div className="text-xs font-semibold uppercase tracking-wide text-[#64748B]">{c.t}</div>
                  {c.href ? <a href={c.href} {...(c.wa ? { target: "_blank", rel: "noopener noreferrer" } : {})} className="mt-0.5 block break-all font-medium text-[#0D1B2A] hover:text-[#245FAF]">{c.d}{c.wa && <span className="ml-2 text-xs font-semibold text-[#128C7E]">Chat now →</span>}</a> : <div className="mt-0.5 font-medium text-[#0D1B2A]">{c.d}</div>}
                </div>
              </div>
            ))}
            <div className="overflow-hidden rounded-2xl border border-[#D7E3F2]">
              <iframe
                title="Heliopolis Pharmaceuticals location"
                data-testid="contact-map"
                className="h-64 w-full"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                src="https://www.openstreetmap.org/export/embed.html?bbox=55.12%2C25.05%2C55.20%2C25.10&layer=mapnik&marker=25.075%2C55.145"
              />
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
